import React, { createContext, useState, useEffect, useCallback } from 'react';

export const LanguageContext = createContext();

const dict = {
  fr: {
    nav: { home: 'Accueil', about: 'À propos', projects: 'Projets', contact: 'Contact' },
    hero: {
      eyebrow: "Cabinet d'architecture",
      tagline: "Concevoir des lieux de vie, de rencontre et d'échange.",
      ctaProjects: 'Voir les projets',
      ctaContact: 'Me contacter',
    },
    intro: {
      title: 'Une architecture à taille humaine',
      text:
        "Chez Fiat Architecte, un bâtiment est plus qu'un simple espace : c'est un lieu de vie, de rencontre et d'échange. Je vous accompagne de la première esquisse jusqu'à la livraison, en plaçant toujours l'humain et l'environnement au centre de la réflexion.",
      values: [
        { title: 'Respect de l’environnement', text: 'Une architecture durable, sobre et attentive à son site.' },
        { title: 'Sur mesure', text: 'Des solutions adaptées aux besoins de chaque client.' },
        { title: 'Équilibre', text: 'Esthétique, confort et performance technique vont ensemble.' },
      ],
    },
    projects: {
      title: 'Projets',
      featured: 'Réalisations',
      intro: 'Une sélection de projets menés au cours de ma carrière.',
      all: 'Tous les projets',
      view: 'Découvrir',
      back: '← Tous les projets',
      next: 'Projet suivant →',
      empty: 'Plus de détails sur ce projet bientôt.',
      factsTitle: 'Fiche technique',
    },
    services: {
      title: 'Services',
      items: [
        { title: 'Conception', text: 'Études de faisabilité et conception architecturale.' },
        { title: 'Autorisations', text: 'Démarches administratives et permis de construire.' },
        { title: 'Suivi de chantier', text: 'Coordination des intervenants et suivi des travaux.' },
        { title: 'Rénovation énergétique', text: 'Conseil en rénovation énergétique et construction durable.' },
      ],
    },
    cta: {
      title: 'Vous avez un projet ?',
      text: 'Une idée, un terrain, une rénovation : parlons-en ensemble.',
      button: 'Prendre contact',
    },
    about: {
      title: 'À propos',
      p1: "Chez Fiat Architecte, un bâtiment est plus qu'un simple espace : c'est un lieu de vie, de rencontre et d'échange.",
      p2: "Mon travail s'étend du logement aux bâtiments publics (écoles, établissements de santé), en passant par la rénovation et la réhabilitation.",
      cabinet: 'Le cabinet',
      visit: 'Le cabinet se situe au',
    },
    contact: {
      title: 'Parlons de votre projet',
      intro: 'Que vous souhaitiez concevoir un espace de vie unique, repenser un lieu de travail ou rénover, je suis là pour vous accompagner.',
      name: 'Nom',
      email: 'Votre email',
      subject: 'Sujet',
      message: 'Message',
      send: 'Envoyer',
      hint: 'Le bouton ouvre votre application de messagerie avec le message prêt à envoyer.',
      directly: 'Ou directement',
      emailLabel: 'Email',
      phoneLabel: 'Téléphone',
      addressLabel: 'Adresse',
      mailBodyFrom: 'De la part de',
    },
    notFound: {
      title: 'Page introuvable',
      text: "La page que vous cherchez n'existe pas ou a été déplacée.",
      button: "Retour à l'accueil",
    },
    footer: { rights: 'Tous droits réservés' },
  },
  en: {
    nav: { home: 'Home', about: 'About', projects: 'Projects', contact: 'Contact' },
    hero: {
      eyebrow: 'Architecture studio',
      tagline: 'Designing places to live, meet and exchange.',
      ctaProjects: 'View projects',
      ctaContact: 'Get in touch',
    },
    intro: {
      title: 'Human-scale architecture',
      text:
        'At Fiat Architect, a building is more than a space: it is a place to live, meet and exchange. I guide you from the first sketch to final delivery, always keeping people and the environment at the heart of the design.',
      values: [
        { title: 'Respect for the environment', text: 'Sustainable, restrained architecture attentive to its site.' },
        { title: 'Tailor-made', text: 'Solutions adapted to each client’s needs.' },
        { title: 'Balance', text: 'Aesthetics, comfort and technical performance go hand in hand.' },
      ],
    },
    projects: {
      title: 'Projects',
      featured: 'Selected work',
      intro: 'A selection of projects completed throughout my career.',
      all: 'All projects',
      view: 'Discover',
      back: '← All projects',
      next: 'Next project →',
      empty: 'More details about this project coming soon.',
      factsTitle: 'Key facts',
    },
    services: {
      title: 'Services',
      items: [
        { title: 'Design', text: 'Feasibility studies and architectural design.' },
        { title: 'Permits', text: 'Administrative procedures and building permits.' },
        { title: 'Site supervision', text: 'Coordination of contractors and monitoring of works.' },
        { title: 'Energy renovation', text: 'Advice on energy renovation and sustainable construction.' },
      ],
    },
    cta: {
      title: 'Have a project?',
      text: 'An idea, a plot of land, a renovation: let’s talk about it.',
      button: 'Contact me',
    },
    about: {
      title: 'About',
      p1: 'At Fiat Architect, a building is more than a space: it is a place to live, meet and exchange.',
      p2: 'My work ranges from housing to public buildings (schools, healthcare facilities), including renovation and rehabilitation.',
      cabinet: 'The practice',
      visit: 'The practice is located at',
    },
    contact: {
      title: 'Let’s talk about your project',
      intro: 'Whether you want to create a unique living space, rethink a workplace or renovate, I am here to help.',
      name: 'Name',
      email: 'Your email',
      subject: 'Subject',
      message: 'Message',
      send: 'Send',
      hint: 'The button opens your email application with the message ready to send.',
      directly: 'Or directly',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      addressLabel: 'Address',
      mailBodyFrom: 'From',
    },
    notFound: {
      title: 'Page not found',
      text: 'The page you are looking for does not exist or has been moved.',
      button: 'Back to home',
    },
    footer: { rights: 'All rights reserved' },
  },
};

const getInitialLanguage = () => {
  try {
    const saved = localStorage.getItem('lang');
    if (saved === 'fr' || saved === 'en') return saved;
  } catch (e) {}
  return 'fr';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    try {
      localStorage.setItem('lang', lang);
    } catch (e) {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t: dict[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};
